import { useEffect, useState, type CSSProperties } from 'react'
import { StoredProfile, updateAnitaUpdatesPreference, updateAvatar } from '@/utils/storage'
import { getNumerologyMeaning, detectMasterNumber } from '@/utils/numerology'
import { PushApiError, getAnitaUpdatesStatus, isPushAvailable, syncAnitaUpdatesPreference } from '@/utils/pushApi'
import { texts } from '@/texts'
import { NotificationSettings } from './NotificationSettings'

interface ProfileViewProps {
  profile: StoredProfile
  onEdit: () => void
  onProfileUpdate?: (profile: StoredProfile) => void
}

interface NumberDetail {
  number: number
  label: string
  icon: string
}

interface PentagonPointProps {
  number: number
  label: string
  icon: string
  style: CSSProperties
  onClick: () => void
}

function PentagonPoint({ number, label, icon, style, onClick }: PentagonPointProps) {
  const meaning = getNumerologyMeaning(number)

  return (
    <div className="mg-point" style={style} onClick={onClick}>
      <div className="mg-orb">
        <span className="ic">{icon}</span>
        <span className="n">{number}</span>
      </div>
      <div className="lab">{label}</div>
      <div className="mn">{meaning.meaning}</div>
    </div>
  )
}

interface NumberOverlayProps {
  detail: NumberDetail
  onClose: () => void
}

function NumberOverlay({ detail, onClose }: NumberOverlayProps) {
  const meaning = getNumerologyMeaning(detail.number)

  return (
    <div className="mg-overlay" onClick={onClose}>
      <div className="mg-sheet" onClick={(e) => e.stopPropagation()}>
        {/* Close Button */}
        <button
          onClick={onClose}
          aria-label={texts.profileView.closeButton}
          style={{
            position: 'absolute',
            top: 'var(--spacing-4)',
            right: 'var(--spacing-4)',
            width: '2.5rem',
            height: '2.5rem',
            padding: 0,
            borderRadius: '50%',
            backgroundColor: 'rgba(232, 200, 122, 0.12)',
            border: '1px solid var(--mg-glass-border)',
            color: 'var(--mg-gold-soft)',
            cursor: 'pointer',
            fontSize: '1.25rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          ✕
        </button>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 'var(--spacing-5)', paddingRight: '2.5rem' }}>
          <div style={{ fontSize: '2.5rem', marginBottom: 'var(--spacing-2)' }}>{detail.icon}</div>
          <h2 style={{
            fontFamily: 'var(--font-serif)',
            fontSize: '1.6rem',
            fontWeight: 'var(--font-weight-bold)',
            color: 'var(--mg-gold-soft)',
            margin: '0 0 var(--spacing-2) 0',
            textShadow: '0 0 16px rgba(232, 200, 122, 0.4)',
          }}>
            {detail.label}
          </h2>
          <div style={{
            fontSize: '3rem',
            fontFamily: 'var(--font-serif)',
            fontWeight: 'var(--font-weight-bold)',
            color: 'var(--mg-gold-soft)',
            lineHeight: 1,
            textShadow: '0 0 24px rgba(232, 200, 122, 0.7), 0 0 60px rgba(180, 120, 230, 0.4)',
          }}>
            {detail.number}
          </div>
        </div>

        {/* Title/Meaning */}
        <h3 style={{
          fontSize: '1.2rem',
          fontWeight: 'var(--font-weight-bold)',
          color: 'var(--mg-gold-soft)',
          margin: '0 0 var(--spacing-4) 0',
          textAlign: 'center',
        }}>
          {meaning.meaning}
        </h3>

        {/* Description */}
        {meaning.description && (
          <div style={{
            marginBottom: 'var(--spacing-5)',
            backgroundColor: 'rgba(232, 200, 122, 0.08)',
            padding: 'var(--spacing-3) var(--spacing-4)',
            borderRadius: 'var(--radius-base)',
            borderLeft: '3px solid var(--mg-gold)',
          }}>
            <p className="mg-body" style={{ fontStyle: 'italic' }}>{meaning.description}</p>
          </div>
        )}

        {/* Characteristics */}
        <div style={{ marginBottom: 'var(--spacing-5)' }}>
          <h3 className="mg-card-title">{texts.profileView.sectionCharacteristics}</h3>
          <div className="mg-chips">
            {meaning.characteristics.map((char) => (
              <span key={char} className="mg-chip">{char}</span>
            ))}
          </div>
        </div>

        {/* Strengths */}
        {meaning.strengths && meaning.strengths.length > 0 && (
          <div style={{ marginBottom: 'var(--spacing-5)' }}>
            <h3 className="mg-card-title">{texts.profileView.sectionStrengths}</h3>
            <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none' }}>
              {meaning.strengths.map((strength) => (
                <li key={strength} className="mg-body" style={{
                  marginBottom: 'var(--spacing-2)',
                  position: 'relative',
                  paddingLeft: 'var(--spacing-4)',
                }}>
                  <span style={{ position: 'absolute', left: 0, color: 'var(--mg-gold)' }}>✦</span>
                  {strength}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Challenges */}
        {meaning.challenges && meaning.challenges.length > 0 && (
          <div style={{ marginBottom: 'var(--spacing-5)' }}>
            <h3 className="mg-card-title">{texts.profileView.sectionChallenges}</h3>
            <ul style={{ margin: 0, paddingLeft: 0, listStyle: 'none' }}>
              {meaning.challenges.map((challenge) => (
                <li key={challenge} className="mg-body" style={{
                  marginBottom: 'var(--spacing-2)',
                  position: 'relative',
                  paddingLeft: 'var(--spacing-4)',
                }}>
                  <span style={{ position: 'absolute', left: 0, color: 'var(--mg-gold)' }}>✦</span>
                  {challenge}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Spiritual */}
        {meaning.spiritual && (
          <div style={{
            marginBottom: 'var(--spacing-5)',
            backgroundColor: 'rgba(150, 90, 220, 0.12)',
            padding: 'var(--spacing-3) var(--spacing-4)',
            borderRadius: 'var(--radius-base)',
            borderLeft: '3px solid rgba(180, 120, 230, 0.8)',
          }}>
            <h3 className="mg-card-title">{texts.profileView.sectionSpiritual}</h3>
            <p className="mg-body">{meaning.spiritual}</p>
          </div>
        )}

        {/* Colors */}
        <div style={{ marginBottom: 'var(--spacing-4)' }}>
          <h3 className="mg-card-title">{texts.profileView.sectionColors}</h3>
          <p className="mg-body">{meaning.colors.join(', ')}</p>
        </div>

        {/* Close */}
        <button className="mg-btn-primary" onClick={onClose} style={{ marginTop: 'var(--spacing-4)' }}>
          {texts.profileView.closeButton}
        </button>
      </div>
    </div>
  )
}

const HINT_SEEN_KEY = 'zahlio_discover_hint_seen'

// --- Avatar (meditierende Figur) -------------------------------------------
// Aufbau: Basis-Emoji + optionaler Hautton-Modifier + optionales Geschlecht.
const AVATAR_BASE = '\u{1F9D8}' // 🧘 Person im Lotussitz
const AVATAR_SKINS = ['', '\u{1F3FB}', '\u{1F3FC}', '\u{1F3FD}', '\u{1F3FE}', '\u{1F3FF}']
const AVATAR_GENDERS: { key: string; suffix: string; label: string }[] = [
  { key: 'female', suffix: '‍♀️', label: '♀' },
  { key: 'male', suffix: '‍♂️', label: '♂' },
  { key: 'neutral', suffix: '', label: '⚲' },
]
const buildAvatar = (genderSuffix: string, skin: string) => AVATAR_BASE + skin + genderSuffix
// Default: weiblich, zweite Option von links (heller Hautton) → 🧘🏻‍♀️
export const DEFAULT_AVATAR = buildAvatar(AVATAR_GENDERS[0].suffix, AVATAR_SKINS[1])

interface AvatarPickerProps {
  current: string
  onSelect: (avatar: string) => void
  onClose: () => void
}

function AvatarPicker({ current, onSelect, onClose }: AvatarPickerProps) {
  return (
    <div className="mg-overlay" onClick={onClose}>
      <div className="mg-sheet" onClick={(e) => e.stopPropagation()}>
        <button
          onClick={onClose}
          aria-label={texts.profileView.closeButton}
          style={{
            position: 'absolute', top: 'var(--spacing-4)', right: 'var(--spacing-4)',
            background: 'none', border: 'none', color: 'var(--mg-text-muted)',
            fontSize: '1.4rem', cursor: 'pointer', lineHeight: 1,
          }}
        >
          ✕
        </button>
        <h3 style={{
          fontFamily: 'var(--font-serif)', color: 'var(--mg-gold-soft)',
          textAlign: 'center', margin: '0 0 var(--spacing-4) 0',
        }}>
          {texts.profileView.avatarPickerTitle}
        </h3>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-3)' }}>
          {AVATAR_GENDERS.map((g) => (
            <div key={g.key}>
              <div style={{
                fontSize: '0.8rem', color: 'var(--mg-text-muted)',
                marginBottom: 'var(--spacing-1)', letterSpacing: '0.05em',
                textAlign: 'center',
              }}>
                {g.label}
              </div>
              <div style={{ display: 'flex', gap: 'var(--spacing-2)', flexWrap: 'wrap', justifyContent: 'center' }}>
                {AVATAR_SKINS.map((skin) => {
                  const emoji = buildAvatar(g.suffix, skin)
                  const isActive = emoji === current
                  return (
                    <button
                      key={skin || 'default'}
                      onClick={() => onSelect(emoji)}
                      aria-label={`${g.label} ${skin ? '' : 'Standard'}`.trim()}
                      style={{
                        fontSize: '1.8rem', lineHeight: 1, padding: 'var(--spacing-2)',
                        cursor: 'pointer', borderRadius: '12px',
                        background: isActive ? 'rgba(232, 200, 122, 0.18)' : 'var(--mg-glass)',
                        border: `1px solid ${isActive ? 'var(--mg-glass-border-strong)' : 'var(--mg-glass-border)'}`,
                      }}
                    >
                      {emoji}
                    </button>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

export function ProfileView({ profile, onEdit, onProfileUpdate }: ProfileViewProps) {
  const [selectedNumber, setSelectedNumber] = useState<NumberDetail | null>(null)
  const [avatarPickerOpen, setAvatarPickerOpen] = useState(false)
  // Discoverability: beim allerersten Profil-View Orbs/Avatar kurz „atmen" lassen
  // und einen einmaligen Hinweis zeigen (danach via LocalStorage-Flag still).
  const [showDiscoverHint, setShowDiscoverHint] = useState(() => {
    try { return !localStorage.getItem(HINT_SEEN_KEY) } catch { return false }
  })
  useEffect(() => {
    if (showDiscoverHint) {
      try { localStorage.setItem(HINT_SEEN_KEY, '1') } catch { /* ignore */ }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const [receiveAnitaUpdates, setReceiveAnitaUpdates] = useState(profile.receiveAnitaUpdates ?? false)
  const [anitaUpdatesBusy, setAnitaUpdatesBusy] = useState(false)
  const [anitaUpdatesError, setAnitaUpdatesError] = useState<string | null>(null)
  const anitaUpdatesAvailable = isPushAvailable()
  const masterNumber = detectMasterNumber(profile.dateOfBirth)

  const formatDate = (dateString: string) => {
    try {
      const date = new Date(dateString + 'T00:00:00')
      return new Intl.DateTimeFormat('de-DE', {
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }).format(date)
    } catch {
      return dateString
    }
  }

  useEffect(() => {
    let active = true

    if (!anitaUpdatesAvailable) return () => { active = false }

    setAnitaUpdatesBusy(true)
    setAnitaUpdatesError(null)

    getAnitaUpdatesStatus()
      .then(({ enabled }) => {
        if (!active) return
        setReceiveAnitaUpdates(enabled)
        const updated = updateAnitaUpdatesPreference(enabled)
        if (updated) onProfileUpdate?.(updated)
      })
      .catch((err) => {
        if (!active) return
        const msg = err instanceof PushApiError ? err.message : texts.errors.unknown
        setAnitaUpdatesError(msg)
      })
      .finally(() => {
        if (active) setAnitaUpdatesBusy(false)
      })

    return () => {
      active = false
    }
  }, [anitaUpdatesAvailable, onProfileUpdate, profile.id])

  const toggleAnitaUpdates = async () => {
    if (anitaUpdatesBusy) return

    const nextValue = !receiveAnitaUpdates
    setAnitaUpdatesError(null)
    setAnitaUpdatesBusy(true)

    try {
      if (anitaUpdatesAvailable) {
        await syncAnitaUpdatesPreference(profile, nextValue)
      }

      setReceiveAnitaUpdates(nextValue)
      const updated = updateAnitaUpdatesPreference(nextValue)
      if (updated) onProfileUpdate?.(updated)
    } catch (err) {
      const msg = err instanceof PushApiError ? err.message : texts.errors.unknown
      setAnitaUpdatesError(msg)
    } finally {
      setAnitaUpdatesBusy(false)
    }
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-6)' }}>
      {/* Profil-Kopf */}
      <div style={{ textAlign: 'center' }}>
        <h2 style={{
          fontFamily: 'var(--font-serif)',
          fontSize: '1.7rem',
          fontWeight: 'var(--font-weight-bold)',
          color: 'var(--mg-gold-soft)',
          margin: '0 0 var(--spacing-1) 0',
          textShadow: '0 0 16px rgba(232, 200, 122, 0.4)',
        }}>
          {profile.name}
        </h2>
        <p style={{ fontSize: '0.9rem', color: 'var(--mg-text-muted)', margin: 0 }}>
          📅 {formatDate(profile.dateOfBirth)}
        </p>
      </div>

      {/* Pentagramm */}
      <div className={`mg-pentagon${showDiscoverHint ? ' mg-discover' : ''}`}>
        <svg viewBox="0 0 100 115">
          <polygon
            points="50,10 92,42 76,100 24,100 8,42"
            fill="none"
            stroke="rgba(232, 200, 122, 0.45)"
            strokeWidth="0.5"
            strokeDasharray="1.5,1.5"
          />
          <line x1="50" y1="60" x2="50" y2="10" stroke="rgba(232, 200, 122, 0.3)" strokeWidth="0.4" />
          <line x1="50" y1="60" x2="92" y2="42" stroke="rgba(232, 200, 122, 0.3)" strokeWidth="0.4" />
          <line x1="50" y1="60" x2="76" y2="100" stroke="rgba(232, 200, 122, 0.3)" strokeWidth="0.4" />
          <line x1="50" y1="60" x2="24" y2="100" stroke="rgba(232, 200, 122, 0.3)" strokeWidth="0.4" />
          <line x1="50" y1="60" x2="8" y2="42" stroke="rgba(232, 200, 122, 0.3)" strokeWidth="0.4" />
        </svg>

        {/* Zentrale Figur */}
        <div className="mg-pent-center">
          <div
            className="fig"
            role="button"
            tabIndex={0}
            aria-label={texts.profileView.avatarPickerTitle}
            style={{ cursor: 'pointer' }}
            onClick={() => setAvatarPickerOpen(true)}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setAvatarPickerOpen(true) }}
          >
            {profile.avatar ?? DEFAULT_AVATAR}
          </div>
          <div className="nm">{profile.name.split(' ')[0]}</div>
        </div>

        {/* Kopf — Lebenszahl */}
        <PentagonPoint
          number={profile.lifePathNumber}
          label={texts.profileView.pentagonLabelLifePath}
          icon="🌟"
          style={{ top: '-4%', left: '50%', transform: 'translate(-50%, 0)' }}
          onClick={() => setSelectedNumber({
            number: profile.lifePathNumber,
            label: texts.profileView.overlayLabelLifePath,
            icon: '🌟',
          })}
        />

        {/* Rechte Hand — Ausdruck */}
        <PentagonPoint
          number={profile.expressionNumber}
          label={texts.profileView.pentagonLabelExpression}
          icon="🎨"
          style={{ top: '41%', right: '-8%', transform: 'translate(0, -50%)' }}
          onClick={() => setSelectedNumber({
            number: profile.expressionNumber,
            label: texts.profileView.overlayLabelExpression,
            icon: '🎨',
          })}
        />

        {/* Rechter Fuß — Geburtstag */}
        <PentagonPoint
          number={profile.birthdayNumber}
          label={texts.profileView.pentagonLabelBirthday}
          icon="🎂"
          style={{ top: '79%', right: '9.5%' }}
          onClick={() => setSelectedNumber({
            number: profile.birthdayNumber,
            label: texts.profileView.overlayLabelBirthday,
            icon: '🎂',
          })}
        />

        {/* Linker Fuß — Seele */}
        <PentagonPoint
          number={profile.soulUrgeNumber}
          label={texts.profileView.pentagonLabelSoul}
          icon="💫"
          style={{ top: '79%', left: '9.5%' }}
          onClick={() => setSelectedNumber({
            number: profile.soulUrgeNumber,
            label: texts.profileView.overlayLabelSoul,
            icon: '💫',
          })}
        />

        {/* Linke Hand — Inneres Selbst */}
        <PentagonPoint
          number={profile.destinyNumber}
          label={texts.profileView.pentagonLabelDestiny}
          icon="🔮"
          style={{ top: '43%', left: '-8%', transform: 'translate(0, -50%)' }}
          onClick={() => setSelectedNumber({
            number: profile.destinyNumber,
            label: texts.profileView.overlayLabelDestiny,
            icon: '🔮',
          })}
        />
      </div>

      {/* Discoverability-Hinweis (nur beim ersten Mal) */}
      {showDiscoverHint && (
        <div className="mg-discover-hint" onClick={() => setShowDiscoverHint(false)}>
          <span>{texts.profileView.discoverHint}</span>
          <button
            className="x"
            aria-label={texts.profileView.closeButton}
            onClick={(e) => { e.stopPropagation(); setShowDiscoverHint(false) }}
          >
            ✕
          </button>
        </div>
      )}

      {/* Meisterzahl */}
      {masterNumber && (
        <div className="mg-card accent" style={{ textAlign: 'center' }}>
          <h3 className="mg-card-title" style={{ marginBottom: '4px' }}>⭐ Meisterzahl {masterNumber}</h3>
          <p className="mg-body sec">
            Deine Quersumme trägt die Meisterzahl {masterNumber} – eine besondere Schwingung in deinem Profil.
          </p>
        </div>
      )}

      {/* Overlay */}
      {selectedNumber && (
        <NumberOverlay detail={selectedNumber} onClose={() => setSelectedNumber(null)} />
      )}

      {/* Avatar-Auswahl */}
      {avatarPickerOpen && (
        <AvatarPicker
          current={profile.avatar ?? DEFAULT_AVATAR}
          onSelect={(avatar) => {
            const updated = updateAvatar(avatar)
            if (updated) onProfileUpdate?.(updated)
            setAvatarPickerOpen(false)
          }}
          onClose={() => setAvatarPickerOpen(false)}
        />
      )}

      {/* Push-Einstellungen */}
      <NotificationSettings
        profile={profile}
        onUpdate={(updated) => onProfileUpdate?.(updated)}
      />

      {/* Infos von Anita */}
      <div className="mg-card">
        <div className="mg-toggle-row">
          <div style={{ flex: 1 }}>
            <h3 className="tt">{texts.profileView.anitaUpdatesTitle}</h3>
            <p className="td">{texts.profileView.anitaUpdatesDescription}</p>
          </div>

          <button
            onClick={toggleAnitaUpdates}
            disabled={anitaUpdatesBusy}
            aria-pressed={receiveAnitaUpdates}
            className={`mg-switch${receiveAnitaUpdates ? '' : ' off'}`}
          >
            <span className="mg-knob" />
          </button>
        </div>

        {!anitaUpdatesAvailable && (
          <p style={{ marginTop: 'var(--spacing-2)', fontSize: '0.8rem', color: 'var(--mg-text-dim)' }}>
            {texts.profileView.anitaUpdatesUnavailableHint}
          </p>
        )}

        {anitaUpdatesError && (
          <p style={{ marginTop: 'var(--spacing-2)', fontSize: '0.85rem', color: '#ff8a8a' }}>
            {anitaUpdatesError}
          </p>
        )}
      </div>

      {/* Bearbeiten */}
      <button className="mg-btn-ghost" onClick={onEdit}>
        {texts.profileView.editProfileButton}
      </button>
    </div>
  )
}
